import React from "react"

import type { BoxProps } from "../Box"

import { Box } from "../Box"
import type { Overwrite } from "utility-types"
import type { SelectId } from "@/theme/form-control/Select"
import type { SensorConfigKey } from "@/base/sensor-configurations"
import type { AddDeviceInputMode } from "@/constants/base"

type InputId =
  | AddDeviceInputMode
  | "address_line_1"
  | "address_line_2"
  | "body"
  | "city"
  | "codeString"
  | "confirmPassword"
  | "country"
  | "deviceName"
  | "email"
  | "feedbackText"
  | "fieldName"
  | "name"
  | "newPassword"
  | "password"
  | "phoneNumber"
  | "postalCode"
  | "radiusInput"
  | "region"
  | "sourceSensor"
  | "sourceSensorSelect"
  | "sourceSensorStateCurrent"
  | "sourceSensorStateCurrentSelect"
  | "sourceSensorStatePrevious"
  | "sourceSensorStatePreviousSelect"
  | "subject"
  | "value"
export type FormFieldId = InputId | SelectId | SensorConfigKey

export interface FormControlProviderProps
  extends Overwrite<BoxProps, { id?: FormFieldId }> {
  autoFocus?: boolean
  isDisabled?: boolean
  isInvalid?: boolean
  isRequired?: boolean
}

function useFormControl({
  autoFocus,
  id,
  isDisabled,
  isInvalid,
  isRequired,
}: FormControlProviderProps) {
  return {
    autoFocus: autoFocus === true,
    id,
    isDisabled: isDisabled === true,
    isInvalid: isInvalid === true,
    isRequired: isRequired === true,
  }
}

type ContextValue = ReturnType<typeof useFormControl>

const Context = React.createContext<ContextValue | undefined>(undefined)

export function Provider({
  autoFocus,
  children,
  id,
  isDisabled,
  isInvalid,
  isRequired,
  ...rest
}: React.PropsWithChildren<FormControlProviderProps>): React.JSX.Element | null {
  const value = useFormControl({
    autoFocus,
    id,
    isDisabled,
    isInvalid,
    isRequired,
  })
  return (
    <Context.Provider value={value}>
      <Box id={id} w={"$full"} {...rest}>
        {children}
      </Box>
    </Context.Provider>
  )
}

export function useFormControlContext(): ContextValue {
  const ctx = React.useContext(Context)

  return {
    autoFocus: ctx?.autoFocus === true,
    id: ctx?.id,
    isDisabled: ctx?.isDisabled === true,
    isInvalid: ctx?.isInvalid === true,
    isRequired: ctx?.isRequired === true,
  }
}
